import type { en } from './en';

/** German copy. Typed against the English dictionary: every key must exist. */
export const de: typeof en = {
  meta: {
    product: 'Hanseatic Select',
    company: 'Hanseatic LifeScience Consulting',
    tagline: 'Software-Lieferantenauswahl auf Basis von Nachweisen',
  },

  common: {
    or: 'oder',
    save: 'Speichern',
    saving: 'Speichert…',
    saved: 'Gespeichert',
    saveFailed: 'Konnte nicht gespeichert werden',
    cancel: 'Abbrechen',
    close: 'Schließen',
    remove: 'Entfernen',
    delete: 'Löschen',
    add: 'Hinzufügen',
    edit: 'Bearbeiten',
    done: 'Fertig',
    open: 'Öffnen',
    back: 'Zurück',
    next: 'Weiter',
    previous: 'Zurück',
    create: 'Anlegen',
    confirm: 'Bestätigen',
    copy: 'Kopieren',
    copied: 'Kopiert',
    copyFailed: 'Kopieren nicht möglich — bitte Text markieren und selbst kopieren',
    copyLink: 'Link kopieren',
    download: 'Herunterladen',
    popupBlocked: 'Der Browser hat den neuen Tab blockiert. Erlauben Sie Pop-ups für diese Website und versuchen Sie es erneut.',
    search: 'Suchen',
    filter: 'Filter',
    all: 'Alle',
    none: 'Keine',
    yes: 'Ja',
    no: 'Nein',
    optional: 'optional',
    required: 'erforderlich',
    loading: 'Lädt…',
    retry: 'Erneut versuchen',
    signIn: 'Anmelden',
    signOut: 'Abmelden',
    email: 'E-Mail',
    password: 'Passwort',
    name: 'Name',
    of: 'von',
    weight: 'Gewichtung',
    vendor: 'Anbieter',
    vendors: 'Anbieter',
    criterion: 'Kriterium',
    criteria: 'Kriterien',
    requirement: 'Anforderung',
    requirements: 'Anforderungen',
    stakeholder: 'Mitwirkende',
    stakeholders: 'Mitwirkende',
    evaluation: 'Bewertung',
    evaluations: 'Bewertungen',
    status: 'Status',
    actions: 'Aktionen',
    language: 'Sprache',
    theme: 'Darstellung',
    themeLight: 'Hell',
    themeDark: 'Dunkel',
    nothingYet: 'Noch nichts vorhanden',
    learnMore: 'Mehr erfahren',
    dismiss: 'Ausblenden',
    print: 'Drucken',
    example: 'Beispiel',
    statusDraft: 'Entwurf',
    statusScoring: 'Bewertung',
    statusLocked: 'Gesperrt',
    statusDecided: 'Entschieden',
  },

  nav: {
    workspace: 'Arbeitsbereich',
    evaluations: 'Bewertungen',
    team: 'Team & Lizenzen',
    settings: 'Einstellungen',
    commandHint: 'Suchen oder springen…',
    commands: 'Befehle',
    openCommand: 'Befehlspalette öffnen',
    menu: 'Menü',
    instanceAdmin: 'Instanzadministration',
    account: 'Konto',
    help: 'Hilfe',
    openWorkspace: 'Arbeitsbereichsmenü öffnen',
  },

  notifications: {
    open: 'Benachrichtigungen',
    title: 'Benachrichtigungen',
    markAllRead: 'Alle als gelesen markieren',
  },

  stages: {
    request: 'Anfrage',
    evidence: 'Nachweise',
    decision: 'Entscheidung',
    requestHint: 'Was beschafft wird, wer anbietet und was zählt.',
    evidenceHint: 'Dokumente, Anbieterantworten und deren unabhängige Auswertung.',
    decisionHint: 'Das dokumentierte Ranking, die Freigabe und das Entscheidungspaket.',
    urs: 'Lastenheft',
    contributors: 'Mitwirkende',
    vendors: 'Anbieter',
    results: 'Ergebnisse',
    ursHint: 'Die Anforderungen, an denen Anbieter gemessen werden.',
    contributorsHint: 'Wer bewertet und mit welchem Stimmgewicht.',
    vendorsHint: 'Wer anbietet und was jede und jeder zurückgesendet hat.',
    resultsHint: 'Wie die Anbieter abschneiden, sobald Mitwirkende bewertet haben.',
    stage: 'Schritt {n}',
    stepOf: 'Schritt {n} von {total}',
    fileDecision: 'Entscheidung dokumentieren',
    fileDecisionBlocked: 'Sperren Sie die Bewertung zuerst. Die Entscheidung wird auf dieser Seite in der Karte „Status“ dokumentiert.',
    weightsUnbalanced: 'Jede Gewichtungsspalte muss zusammen 100% ergeben, um fortzufahren',
    done: 'erledigt',
    current: 'aktuell',
    todo: 'offen',
  },

  evidence: {
    title: 'Nachweise',
    lead: 'Was die Nachweise sagen — getrennt von dem, was die Personen sagten.',
    aiBadge: 'KI',
    documents: 'Dokumente',
    independent: 'Unabhängige Auswertung',
    matrix: 'Compliance-Matrix',
    clarify: 'Nachfassen & Demo',
    commercial: 'Kommerziell',
    aiHalfTitle: 'KI-Analyse',
    resultsHalfTitle: 'Bewertungsergebnisse',
    findingsTitle: 'Befunde',
    findingsEmpty: 'Noch keine Befunde erfasst.',
    include: 'Übernehmen',
    discard: 'Verwerfen',
    included: 'Übernommen',
    discarded: 'Verworfen',
    scoringRecord: 'Der Bewertungsdatensatz',
  },

  auth: {
    workspaceLead: 'Melden Sie sich bei {workspace} an. Diese Adresse dient nur Ihrem Workspace.',
    signInTitle: 'Anmelden',
    signInLead: 'Mit E-Mail und Passwort für den Arbeitsbereich Ihrer Organisation.',
    registerTitle: 'Arbeitsbereich anlegen',
    registerLead: 'Sie werden erster Eigentümer. Kolleginnen und Kollegen kommen per Einladung dazu.',
    orgName: 'Organisation',
    yourName: 'Ihr Name',
    workEmail: 'Dienstliche E-Mail',
    createWorkspace: 'Arbeitsbereich anlegen',
    noAccount: 'Noch kein Arbeitsbereich?',
    haveAccount: 'Sie haben bereits einen Arbeitsbereich?',
    forgot: 'Passwort vergessen?',
    forgotHelp:
      'Bitten Sie einen Eigentümer Ihrer Organisation um einen Reset-Link unter „Team & Lizenzen“. Sind Sie der einzige Eigentümer, wenden Sie sich an HLSC.',
    trialNote: 'Enthält {days} Tage Zugang zu allen Werkzeugen, KI inklusive. Keine Kreditkarte.',
    passwordHint: 'Mindestens 10 Zeichen.',
    signInMicrosoft: 'Mit Microsoft anmelden',
    microsoftHint: 'Öffnen Sie Hanseatic Select in Microsoft Teams mit Ihrem Arbeitskonto.',
    inviteTitle: '{org} beitreten',
    inviteLead: 'Vergeben Sie ein Passwort für Ihren Platz als {plan}.',
    inviteAccept: 'Arbeitsbereich beitreten',
    resetTitle: 'Neues Passwort setzen',
    resetLead: 'Wählen Sie ein neues Passwort für {email}.',
    resetCta: 'Passwort speichern und anmelden',
    signedOut: 'Sie sind abgemeldet.',
    devPreview: 'Lokale Vorschau',
    acceptTerms: 'Ich akzeptiere die {terms} und die {privacy}.',
    termsLinkLabel: 'AGB',
    privacyLinkLabel: 'Datenschutzerklärung',
    registerPending: 'Prüfen Sie Ihr Postfach, um fortzufahren — oder melden Sie sich an, falls Sie bereits einen Arbeitsbereich haben.',
    workspaceCreatedTitle: 'Arbeitsbereich angelegt',
    verifyRequiredTitle: 'E-Mail-Adresse bestätigen',
    verifyLinkFieldLabel: 'Bestätigungslink',
    verifyLinkShownOnce: 'Wird nur einmal angezeigt — jetzt kopieren.',
    confirmAndContinue: 'Bestätigen und fortfahren',
    continueToWorkspace: 'Weiter zum Arbeitsbereich',
    resendLink: 'Link erneut senden',
    verifiedTitle: 'E-Mail bestätigt',
    resendSent: 'Prüfen Sie Ihr Postfach auf einen neuen Link.',
  },

  sso: {
    continueButton: 'Mit Microsoft fortfahren',
    noAccount: 'Für diese Adresse gibt es noch keinen Zugang. Bitten Sie eine:n Eigentümer:in um eine Einladung und versuchen Sie es erneut.',
    cancelled: 'Die Anmeldung mit Microsoft wurde abgebrochen.',
    noWorkspace: 'Für diese E-Mail-Adresse wurde kein Arbeitsbereich gefunden. Melden Sie sich stattdessen mit Passwort an.',
  },

  home: {
    title: 'Ihre Bewertungen',
    lead: 'Workshop-Datei öffnen, weiter bewerten oder eine neue Auswahl starten.',
    newEvaluation: 'Neue Bewertung',
    fromDocuments: 'Mit einem Lastenheft starten',
    fromDocumentsHint: 'Lastenheft hochladen und in rund zwei Minuten einen gewichteten Kriterienbaum erhalten.',
    empty: 'Noch keine Bewertungen.',
    emptyLead: 'Starten Sie mit Ihrem Lastenheft oder legen Sie die Kriterien selbst an.',
    activeEvaluations: 'Laufende Bewertungen',
    vendorsCompared: 'Verglichene Anbieter',
    stakeholdersScoring: 'Bewertende Mitwirkende',
    decisionsFiled: 'Dokumentierte Entscheidungen',
    checklistTitle: 'Zu einer belastbaren Entscheidung',
    checklistLead: 'Fünf Schritte. Die Reihenfolge ist frei, das hier ist der kurze Weg.',
    checklistCreate: 'Bewertung anlegen',
    checklistCriteria: 'Kriterien und Gewichte abstimmen',
    checklistContributors: 'Bewertende Mitwirkende einladen',
    checklistEvidence: 'Nachweise der Anbieter sammeln',
    checklistDecision: 'Entscheidung dokumentieren',
    contributorTitle: 'Ihre Bewertungskarte',
    contributorLead: 'Geben Sie die PIN ein, die Sie erhalten haben.',
    pinLabel: 'PIN',
    openScorecard: 'Bewertungskarte öffnen',
    trialBanner: 'KI-Test: noch {days} Tage.',
    trialEnded: 'Ihr Test ist beendet. Ihre Bewertungen bleiben erhalten; für die KI-Werkzeuge sprechen Sie mit HLSC.',
    trialCta: 'KI-Werkzeuge behalten',
    demoBanner: 'Demo-Arbeitsbereich — verschwindet {when}. Nichts davon landet in Ihrer Organisation.',
    demoCta: 'Kostenlosen Arbeitsbereich anlegen',
    openEvaluationAria: '{name} öffnen',
    deleteCta: 'Bewertung löschen',
    deleteTitle: 'Diese Bewertung löschen?',
    deleteBody: '{name} und alle darin erfassten Bewertungen werden entfernt. Das lässt sich nicht rückgängig machen.',
    deleteConfirm: 'Bewertung löschen',
    deletedToast: 'Bewertung gelöscht.',
    archiveCta: 'Bewertung archivieren',
    archiveTitle: 'Diese Bewertung archivieren?',
    archiveBody: 'Für {name} liegt eine unterschriebene Entscheidung vor, daher bleibt sie als Nachweis erhalten, statt gelöscht zu werden. Sie verlässt Ihre Arbeitsbereich-Liste und lässt sich später wiederherstellen.',
    archiveConfirm: 'Bewertung archivieren',
    archivedToast: 'Bewertung archiviert.',
    archivedTitle: 'Archivierte Bewertungen ({n})',
    restoreCta: 'Wiederherstellen',
    restoredToast: 'Bewertung in Ihren Arbeitsbereich zurückgeholt.',
  },

  wizard: {
    title: 'Neue Bewertung',
    lead: 'Zwei kurze Schritte. Alles lässt sich später ändern.',
    step1: 'Was beschaffen Sie?',
    step2: 'Welche Kriterien?',
    projectName: 'Name der Bewertung',
    projectNamePlaceholder: 'MES-Auswahl — Abfüllung',
    client: 'Kunde oder Standort',
    clientPlaceholder: 'Werk Hamburg',
    objective: 'Was muss der gewählte Anbieter leisten?',
    objectivePlaceholder:
      'Ein GxP-MES für die sterile Abfülllinie: elektronische Chargenprotokolle, Part-11-Signaturen, SAP-Anbindung und ein GAMP-5-Validierungspaket.',
    addVendor: 'Weiteren Anbieter hinzufügen',
    templateLead: 'Mit einer HLSC-Kriterienvorlage starten oder mit einem leeren Baum.',
    templateBlank: 'Standardbaum (4 Abschnitte)',
    createCta: 'Bewertung anlegen',
    needName: 'Geben Sie der Bewertung einen Namen.',
  },

  draft: {
    unreadableWord: 'Word-Dateien älter als .docx können hier nicht gelesen werden. Bitte als .docx oder PDF speichern.',
    unreadableBinary: 'Diese Datei ist kein lesbares Dokument. Bitte PDF, Word (.docx), TXT oder Markdown verwenden.',
    title: 'Mit einem Lastenheft starten',
    lead: 'Laden Sie Lastenheft und Ausschreibung hoch. Die Engine extrahiert die Anforderungen, gruppiert sie und schlägt gewichtete Kriterien vor. Sie geben jede Zeile frei.',
    dropzone: 'Lastenheft hier ablegen oder Dateien wählen',
    dropzoneHint: 'PDF, Word (.docx), TXT oder MD, je bis 15 MB. Sie werden im Browser gelesen.',
    kindUrs: 'Lastenheft',
    kindRfp: 'Ausschreibung',
    kindOther: 'Sonstiges',
    files: 'Dokumente',
    noFiles: 'Noch keine Dokumente.',
    hintName: 'Namensvorschlag',
    hintClient: 'Kunde',
    hintTemplate: 'Vorlagenhinweis',
    run: 'Mit KI entwerfen',
    running: 'Entwurf läuft…',
    reviewTitle: 'Entwurf prüfen',
    reviewLead: 'Nichts wird angelegt, bevor Sie freigeben. Gewichte sind Prozentwerte — ändern Sie, was nicht passt.',
    approve: 'Freigeben und anlegen',
    discard: 'Verwerfen',
    addGroup: 'Abschnitt hinzufügen',
    addCriterion: 'Kriterium hinzufügen',
    expectation: 'Woran gutes Erfüllen erkennbar ist',
    sources: 'Quellen',
    requirementsFound: '{n} Anforderungen gefunden',
    warnings: 'Hinweise',
    createdBanner:
      'Aus Ihren Dokumenten entworfen. Sie haben {criteria} Kriterien in {groups} Abschnitten freigegeben; die Quellen bleiben bei der Bewertung.',
    needDocuments: 'Fügen Sie mindestens ein Dokument hinzu.',
    tooLarge: 'Diese Datei überschreitet 15 MB Text. Teilen Sie das Lastenheft oder laden Sie einen Auszug hoch.',
    clipped: 'Es wurden nur die ersten {n} Zeichen dieses Dokuments verwendet.',
  },

  request: {
    sourceTitle: 'Anforderungsdokument',
    sourceDrop: 'URS oder RFP hierher ziehen oder klicken',
    draftWithAi: 'Kriterien aus diesem Dokument erstellen',
    draftConfirm: 'Die Kriterien unten durch die aus diesem Dokument erstellten ersetzen?',
    drafted: '{n} Kriterien erstellt',
    sectionsTotal: 'Abschnitte',
    criteriaTotal: 'Kriterien',
    title: 'Anfrage',
    lead: 'Was beschafft wird, wer anbietet und was zählt.',
    details: 'Eckdaten der Bewertung',
    scale: 'Bewertungsskala',
    scaleMin: 'Niedrig (niedrigste Bewertung)',
    scaleMax: 'Hoch (höchste Bewertung)',
    scaleErrorWhole: 'Niedrig und Hoch müssen ganze Zahlen sein.',
    scaleErrorBounds: 'Niedrig und Hoch müssen jeweils zwischen {floor} und {ceiling} liegen.',
    scaleErrorOrder: 'Niedrig muss kleiner als Hoch sein.',
    scaleMaxLocked: 'Die Bewertungsskala lässt sich nicht mehr ändern, sobald Bewertungen vorliegen, denn dieselbe Zahl würde auf einer anderen Skala etwas anderes bedeuten.',
    objective: 'Erwartungen / Kontext der Ausschreibung',
    criteriaTitle: 'Kriterien und Gewichte',
    criteriaLead: 'Gewichte sind Prozentwerte eines Ganzen. Jede Spalte sollte zusammen 100 % ergeben.',
    groupWeight: 'Abschnittsgewicht',
    share: 'Anteil',
    weightsOk: 'Die Gewichte sind stimmig.',
    addGroup: 'Abschnitt hinzufügen',
    addCriterion: 'Kriterium hinzufügen',
    expectationLabel: 'Woran gutes Erfüllen erkennbar ist',
    expectationPlaceholder: 'Welchen Nachweis muss ein Anbieter zeigen, um hier gut abzuschneiden?',
    membersTitle: 'Wer diese Bewertung bearbeiten darf',
    membersLead: 'Mitglieder mit Eigentümer-Lizenz dürfen bearbeiten; Contributors bewerten nur ihre eigene Karte.',
    memberEmail: 'Dienstliche E-Mail',
    membersHint: 'Eigentümer:innen führen die Bewertung und brauchen hier ein Konto. Mitwirkende nicht — fügen Sie sie unter Mitwirkende hinzu, wo jede:r einen eigenen Link und eine PIN erhält.',
    addMember: 'Mitglied hinzufügen',
    activity: 'Aktivität',
    activityLead: 'Jede Änderung an dieser Bewertung, neueste zuerst.',
  },

  assistant: {
    title: 'Assistent',
    open: 'Assistenten öffnen',
    send: 'Senden',
    proposed: 'Vorgeschlagene Änderung',
    confirm: 'Ausführen',
    placeholder: 'Fragen Sie zur Bewertung oder sagen Sie, was geändert werden soll',
    dock: 'Seitlich andocken',
    undock: 'In die Ecke verkleinern',
  },
  seats: {
    title: 'Mitwirkende',
    lead: 'Die Personen, die bewerten. Jede erhält eine private Karte, die niemand sonst sieht.',
    addSeat: 'Mitwirkende hinzufügen',
    seatName: 'Rolle oder Name',
    voteWeight: 'Stimmgewicht',
    voteWeightHint: 'Wie stark diese Karte im dokumentierten Ranking zählt.',
    statusIssued: 'Eingeladen',
    statusClaimed: 'Bewertet',
    empty: 'Noch keine Karten. Fügen Sie Qualität, Produktion, IT, Technik hinzu…',
    coverage: 'Bewertungsfortschritt',
    cells: '{n} Felder auf dieser Karte',
    colPerson: 'Person',
    colFunction: 'Funktion',
    colStatus: 'Status',
    colInvitation: 'Einladung',
    namePlaceholder: 'Vollständiger Name',
    emailLabel: 'E-Mail',
    emailPlaceholder: 'name@firma.de',
    functionLabel: 'Funktion',
    functionPlaceholder: 'Qualität, Produktion, IT …',
    copyLink: 'Link kopieren',
    sendByEmail: 'Per E-Mail senden',
    remind: 'Erinnerung senden',
    reminded: 'Erinnerung gesendet',
    reissueLink: 'Neuen Link ausstellen (der alte funktioniert dann nicht mehr)',
    reissuePin: 'Neuen Zugangscode ausstellen (der alte funktioniert dann nicht mehr)',
    linkModalTitle: 'Diesen Link an {name} senden',
    linkModalLead: 'Er wird nur einmal angezeigt. Kein Konto nötig — der Link führt direkt zur eigenen Karte, sobald der Zugangscode eingegeben ist, den Sie separat weitergeben.',
    pinModalTitle: 'Neuer Zugangscode für {name}',
    pinModalLead: 'Er wird nur einmal angezeigt.',
    pinFieldLabel: 'Zugangscode',
    copyPin: 'Code kopieren',
    pinCopied: 'Zugangscode kopiert.',
    pinSeparateWarning: 'Geben Sie {name} diesen Code auf einem anderen Weg als den Link — per Anruf, Nachricht oder persönlich. Niemals in derselben E-Mail oder Nachricht: Wer beides hat, kann diese Karte öffnen.',
    statusScored: 'Bewertet',
    statusInvited: 'Eingeladen',
    statusNotInvited: 'Nicht eingeladen',
    privacyNote: 'Bewertungen bleiben bis zum Abschluss für jede Person privat. Weder für Sie noch für einen Betreiber oder den Assistenten sichtbar.',
    inviteCopied: 'Einladungslink kopiert.',
    countLabel: '{n} Beitragende',
  },

  urs: {
    title: 'Lastenheft',
    // Zustand 1 — noch nichts hochgeladen.
    dropzone: 'Lastenheft hochladen oder mit einer Vorlage beginnen.',
    dropzoneHint: 'PDF oder Word (.docx), bis 15 MB. Wird im Browser gelesen — nichts wird hochgeladen, bevor eine Datei gewählt ist.',
    startFromTemplate: 'Mit Vorlage beginnen',
    chooseTemplate: 'Vorlage wählen',
    // Zustand 2 — Extraktion.
    extracting: 'Dokument wird gelesen…',
    // Zustand 3 — die Prüftabelle.
    reviewTitle: 'Anforderungen',
    uploadAnother: 'Weiteres Dokument hochladen',
    extractFromStored: 'Aus URS extrahieren',
    storedUnreadable: 'Dieses Dokument enthält keinen lesbaren Text.',
    colGroup: 'Gruppe',
    colRequirement: 'Anforderung',
    colWeight: 'Gewicht',
    colPriority: 'Muss / Soll',
    colSource: 'Quelle',
    priorityMust: 'Muss',
    priorityShould: 'Soll',
    addRow: 'Anforderung hinzufügen',
    newGroup: 'Neue Gruppe',
    addedByYou: 'Von dir hinzugefügt',
    removedRow: 'Anforderung entfernt.',
    balance: 'Abschnitte ausgleichen',
    undo: 'Rückgängig',
    save: 'Speichern',
    noRows: 'Noch keine Anforderungen.',
  },
  vendors: {
    title: 'Anbieter',
    lead: 'Wer anbietet, was Sie angefordert haben, und was zurückgekommen ist.',
    addVendor: 'Anbieter hinzufügen',
    vendorNameLabel: 'Anbietername',
    colName: 'Anbieter',
    colContact: 'Kontakt',
    colStatus: 'Portalstatus',
    colSubmitted: 'Eingereicht',
    statusNotInvited: 'Nicht eingeladen',
    manageLink: 'Link verwalten',
    empty: 'Noch keine Anbieter. Fügen Sie die bietenden Unternehmen hinzu.',
    selectVendor: 'Anbieter auswählen, um Dokumente, Rückfragen und kommerzielle Zahlen zu sehen.',

    requestsTitle: 'Anfragen an Anbieter',
    documentsHeading: 'Angeforderte Dokumente',
    questionsHeading: 'Gestellte Fragen',
    documentsEmpty: 'Noch keine Dokumente angefordert. Ergänzen Sie, was jeder Anbieter einreichen muss.',
    questionsEmpty: 'Noch keine Fragen. Ergänzen Sie, was jeder Anbieter beantworten soll.',
    requestDocument: 'Dokument anfordern',
    askQuestion: 'Frage stellen',
    questionLabel: 'Frage',
    questionPlaceholder: 'Wie viele validierte Standorte betreiben diese Version?',
    answerFormatLabel: 'Antwortformat',
    requestNameLabel: 'Dokumentname',
    requestNamePlaceholder: 'Validierungsbericht',
    requestDescriptionLabel: 'Beschreibung',
    requestMandatoryLabel: 'Pflicht',
    kindFile: 'Datei-Upload',
    kindText: 'Kurze Antwort',
    kindLongtext: 'Antwort als Absatz',
    kindNumber: 'Zahl',
    kindDate: 'Datum',
    kindChoice: 'Eine Option wählen',
    kindBoolean: 'Ja / Nein',
    requestFormatsLabel: 'Akzeptierte Formate',
    requestOptionsLabel: 'Optionen (eine pro Zeile)',
    requestAppliesToLabel: 'Gilt für',
    appliesAll: 'Alle Anbieter',
    appliesSpecific: 'Bestimmte Anbieter',
    moveUp: 'Nach oben',
    moveDown: 'Nach unten',
    mandatoryBadge: 'Pflicht',
    optionalBadge: 'Optional',
    saveAsTemplate: 'Liste als Vorlage speichern',
    templateNameLabel: 'Vorlagenname',
    templateNamePlaceholder: 'Standard-Dokumentenpaket',
    applyTemplateLabel: 'Anfragen aus einer Vorlage hinzufügen',
    applyNoVendors: 'Noch keine Anbieter. Diese Anfragen gehen an jeden Anbieter, den Sie hinzufügen.',
    applyTemplateCta: 'Hinzufügen',
    noTemplates: 'Noch keine Vorlagen.',
    templateSaved: 'Vorlage gespeichert',
    templateApplied: 'Vorlage angewendet',
    sendTitle: 'Link erzeugen und senden',

    tabDocuments: 'Dokumente',
    tabQuestions: 'Nachfassen',
    tabCommercial: 'Kommerziell',
    previewTitle: 'Vorschau — was der Anbieter sieht',
    edit: 'Bearbeiten',

    checklistNotUploaded: 'Noch nicht hochgeladen',
    aiAllowedLabel: 'Für die KI-Analyse freigeben',
    view: 'Ansehen',
    otherDocuments: 'Weitere Dokumente',
    uploadedAt: 'Hochgeladen am {date}',
    portalLinkTitle: 'Portal-Link',
    portalManage: 'In Dokumente verwalten',

    countLabel: '{n} Anbieter',
    generateForm: 'Anbieterformular erzeugen',
    formTitle: 'Anbieterformular — {vendor}',
    formLead: 'Dieses Formular füllt {vendor} aus. Erzeugen Sie einen Link zum Versenden.',
    formRequirementsTitle: 'Zu beantwortende Anforderungen',
    formRequirementsSource: '{count} freigegebene Anforderungen · Quelle: {source}',
    formRequirementsEmpty: 'Noch keine freigegebenen Anforderungen — ergänzen Sie sie in Schritt 2, URS.',
    formAttachmentsTitle: 'Angeforderte Dokumente und Angaben',
    formAttachmentsEmpty: 'Noch keine Dokumentenanforderungen — ergänzen Sie sie oben.',
    formVendorFills: 'Vom Anbieter auszufüllen',
    formContactTitle: 'Senden an',
    generateLink: 'Link erzeugen',
    linkReady: 'Der Link ist der Zugang — er funktioniert ohne Konto und nur für diesen Anbieter.',
    colRequirementId: 'ID',
    colRequirement: 'Anforderung',
    colSection: 'Abschnitt',
  },
  results: {
    colScored: 'Bewertet',
    scoredOf: '{scored} von {total}',
    title: 'Bewertungsergebnisse',
    lead: 'Was die Bewertung ergeben hat. Einzelantworten erscheinen erst nach dem Abschluss.',
    openBanner: 'Die Bewertung ist offen. Einzelne Antworten bleiben verborgen, bis sie gesperrt wird.',
    lockAction: 'Bewertung sperren',
    lockConfirmBody: 'Dies gibt die Antworten jeder:jedes Mitwirkenden einzeln sichtbar und kann nicht zurückgenommen werden. Bewertung für diese Evaluierung sperren?',
    lockedToast: 'Die Bewertung ist gesperrt.',
    lockedBadge: 'Bewertung gesperrt',
    rankingTitle: 'Anbieter-Rangfolge',
    marginLabel: '{leader} liegt {gap} vor {runnerUp}.',
    scoreLegend: 'Bewertung',
    spreadTitle: 'Uneinigkeit je Kriterium',
    perContributorTitle: 'Je Mitwirkende:r',
    noContributors: 'Noch hat niemand bewertet.',
  },

  documents: {
    title: 'Dokumente',
    lead: 'Lastenheft, Ausschreibung und die Rückläufer der Anbieter. Alles bleibt in Ihrem Arbeitsbereich.',
    addTitle: 'Dokument hinzufügen',
    kindUrs: 'Lastenheft',
    kindRfp: 'Ausschreibung',
    kindTechnical: 'Technisches Angebot',
    kindCommercial: 'Kommerzielles Angebot',
    kindQa: 'Rückfragen',
    kindPresentation: 'Präsentation',
    kindDemoNotes: 'Demo-Notizen',
    kindRequirements: 'Anforderungsliste',
    forVendor: 'Für Anbieter',
    dropOrPaste: 'Datei ablegen oder Text einfügen',
    pastePlaceholder: 'Dokumenttext einfügen',
    stored: 'Abgelegte Dokumente',
    noDocuments: 'Noch keine Dokumente. Beginnen Sie mit dem Lastenheft oder der Ausschreibung.',
    requirementsGroup: 'Anforderungsliste',
    fromUrsStep: 'Wird im URS-Schritt hinzugefügt.',
    fromVendorLink: 'Kommt über den Link dieses Anbieters.',
    otherDocuments: 'Auf anderem Weg übermittelt',
    characters: '{n} Zeichen',
    templateCta: 'Antwortvorlage herunterladen',
    portalTitle: 'Anbieter direkt fragen',
    portalLead:
      'Senden Sie jedem Anbieter einen privaten Link. Dort beantwortet er die Anforderungsliste und lädt sein Angebot hoch — ohne Konto.',
    portalInvite: 'Link erzeugen',
    portalContact: 'Ansprechpartner',
    portalEmail: 'E-Mail des Ansprechpartners',
    portalAnswered: '{answered} von {total} beantwortet',
    portalStatusSent: 'Link gesendet',
    portalStatusOpened: 'Geöffnet',
    portalStatusInProgress: 'In Bearbeitung',
    portalStatusSubmitted: 'Eingereicht',
    portalStatusLate: 'Überfällig',
    portalStatusRevoked: 'Zurückgezogen',
    portalStatusExpired: 'Abgelaufen',
    portalRevoke: 'Link zurückziehen',
    portalNewLink: 'Neuer Link',
    portalLinkTitle: 'Diesen Link an {vendor} senden',
    portalLinkLead: 'Er wird einmal angezeigt. Gespeichert wird nur ein Hash, ein Ersatzlink lässt sich jederzeit erzeugen.',
    portalCopied: 'Anbieter-Link kopiert',
    portalEmpty: 'Noch keine Anbieter-Links.',
    portalReopen: 'Antwort wiedereröffnen',
    portalSubmissions: '{n} Einreichungen',
    portalReopenedAt: 'Wiedereröffnet am {date}',
    portalDueLabel: 'Frist',
    portalDueAt: 'Frist {date}',
    portalRemind: 'Erinnerung senden',
    portalReminded: 'Erinnerung gesendet',
    portalRemindedAt: 'Erinnerung gesendet am {date}',
    portalReissue: 'Neuen Link ausstellen (der alte funktioniert dann nicht mehr)',
  },

  ai: {
    title: 'Unabhängige Auswertung',
    lead: 'Eine nachvollziehbare Lesart der Angebote: erst Begriffsabdeckung, dann Sprachprüfung, dann eine kalibrierte Zahl. Das Modell schreibt nie die Note.',
    run: 'Unabhängige Auswertung starten',
    running: 'Läuft…',
    lastRun: 'Letzter Lauf {duration} · {engine}',
    showSteps: 'Schritte anzeigen',
    hideSteps: 'Schritte ausblenden',
    tabRequirements: 'Anforderungsabdeckung',
    tabGaps: 'Abweichungen',
    tabScores: 'Unabhängige Bewertung',
    tabIntegrity: 'Integrität',
    tabFollowUps: 'Nachfassen',
    followUpsClear: 'Jede Anforderung wird von mindestens einem Angebot ausführlich beantwortet. Es bleibt nichts nachzufassen.',
    followUpUnanswered: 'Von niemandem beantwortet',
    followUpThin: 'Nur vage beantwortet',
    followUpSole: 'Nur ein Angebot',
    followUpDeferred: 'Zurückgestellt',
    followUpMustCount: 'davon {n} Muss-Anforderungen',
    followUpUnansweredTitle: 'Diese Anforderungen beantwortet kein Angebot',
    followUpUnansweredLead: 'In keiner angenommenen Antwort findet sich etwas zu diesen Anforderungen. Nehmen Sie sie in die Klärungsrunde auf — oder streichen Sie sie, falls sie nicht mehr gelten.',
    followUpThinTitle: 'Beantwortet, aber nicht nachprüfbar',
    followUpThinLead: 'Diese Punkte werden angesprochen, aber kein Angebot antwortet konkret genug, um es zu überprüfen. Fragen Sie nach dem Wie, nicht nach dem Ob.',
    followUpDeferredTitle: 'Zurückgestellt oder ausgeschlossen',
    followUpDeferredLead: 'Der Anbieter selbst verweist auf eine Roadmap, schließt es aus oder berechnet es zusätzlich. Klären Sie Termin und Preis, bevor daraus eine Annahme wird.',
    followUpSoleTitle: 'Nur ein Angebot deckt dies ab',
    followUpSoleLead: 'Nur ein Anbieter beantwortet diese Anforderungen. Das ist entweder ein echtes Unterscheidungsmerkmal oder eine Lücke, die die anderen nie schließen sollten — entscheiden Sie das, bevor es den Zuschlag bestimmt.',
    tabRecommendation: 'Empfehlung',
    needDocuments: 'Laden Sie zuerst mindestens ein Anbieterangebot hoch.',
    howTitle: 'Wie die Zahl entsteht',
    howLead:
      'Die lexikalische Prüfung zählt Kriterienbegriffe im Angebot. Die Sprachprüfung kennzeichnet jedes Feld als bestätigend, widersprechend, gemischt oder fehlend, inklusive Verneinungen. Die kalibrierte Note besteht zu 35 % aus der lexikalischen und zu 65 % aus der sprachlichen Bewertung. Zitate, die nicht wörtlich im Angebot stehen, werden verworfen.',
    coverage: 'Abdeckung',
    quotes: 'Zitate',
    noQuotes: 'Für dieses Feld liegen keine Zitate vor.',
    integrityTitle: 'Glaubwürdigkeit der Karten',
    integrityLead:
      'Nur Fakten. Eine auffällige Karte zählt in der beratenden Empfehlung weniger. Niemand wird beschuldigt: Bewertungen werden mit dem Gremium und den Angeboten verglichen.',
    overallRisk: 'Gesamtrisiko Integrität',
    integrityBandHigh: 'Karten mit hohem Risiko',
    integrityBandMedium: 'Karten mit mittlerem Risiko',
    integrityBandLow: 'Karten mit geringem Risiko',
    findings: 'Befunde',
    noFindings: 'Keine Auffälligkeiten.',
    recommendationTitle: 'Faktengewichtete Empfehlung',
    advisory: 'Beratend',
    advisoryNote: 'Nur beratend. Dokumentiert wird das gewichtete Mittel der Mitwirkenden.',
    recommended: 'Empfohlen',
    tooClose: 'Zu knapp für eine Empfehlung',
    tooCloseHint: 'allein anhand der Unterlagen',

    evidenceWeight: 'Gewicht der Nachweise',
    expectationMatch: 'Erfüllung der Erwartungen',
    expectationsMet: 'Erfüllte Erwartungen',
    expectationGaps: 'Offene Erwartungen',
    caveats: 'Einschränkungen',
    combinedRanking: 'Kombiniertes Ranking',
    independent: 'Unabhängig',
    stakeholdersColumn: 'Mitwirkende',
    finalColumn: 'Gesamt',
    gapsLead:
      'Wo die unabhängige Lesart und das Gremium auseinandergehen. Markierte Felder kommen in den Workshop und ins Entscheidungspaket.',
    gapFlag: 'Markieren',
    gapFlagged: 'Markiert',
    gapNote: 'Workshop-Notiz',
    gapEmpty: 'Keine Abweichungen für diesen Filter.',
    aiEvidence: 'KI-Nachweise',
    notRun: 'Noch nicht ausgeführt.',
    notRunLead: 'Laden Sie die Angebote hoch und starten Sie die unabhängige Auswertung.',
    irrelevant: 'Dieses Dokument wirkt nicht wie ein Angebot zu dieser Bewertung.',
    aiSplitRaised: 'Über den Standard-Split von 45/55 hinaus angehoben, weil {reason}.',
    aiSplitReasonCoverage: 'die Abdeckung niedrig ist',
    aiSplitReasonBias: 'eine Verzerrung im Gremium erkannt wurde',
    aiSplitReasonBoth: 'die Abdeckung niedrig ist und eine Verzerrung im Gremium erkannt wurde',

    // KI-Doppelmodus (Flow-Upgrade, 5. Okt. 2026): der KI-Mitwirkende
    // (Modus 1) und die KI-Analyse der Bewertungen (Modus 2), sowie die
    // beiden Schalter der Entscheidungsseite. Inklusive Form, wie im
    // übrigen Haus-Glossar.
    contributionTitle: 'KI-Beitrag',
    contributorSeatLabel: 'KI-Mitwirkende:r',
    contributorCountedToggle: 'Die Bewertungen der KI-Mitwirkenden ins Ranking einbeziehen',
    analysisIncludedToggle: 'Die KI-Analyse als Anhang beifügen',
    countedOff: 'Aus (Standard): Die KI bewertet blind und unabhängig von den Personen, ihre Bewertungen bleiben aber aus dem Ranking heraus.',
    countedOn: 'An: Die Bewertungen der KI gehen wie die einer weiteren Person in den gewichteten Mittelwert ein, neben denen der menschlichen Mitwirkenden.',
    countedCoverage: 'In beiden Fällen zählt die KI nie zur Abdeckung. Nur Menschen zählen — das Einschalten kann eine dünn bewertete Evaluierung also nicht vollständig aussehen lassen.',
    recomputing: 'Ranking wird aktualisiert …',
    countedNowOn: 'Der KI-Beitrag zählt jetzt im Ranking.',
    countedNowOff: 'Das Ranking zählt den KI-Beitrag nicht mehr.',
    // Der Lauf der KI-Mitwirkenden und die schlichten Tatsachen, von denen der Schalter abhängt.
    contributorRun: 'KI-Bewertung starten',
    contributorRerun: 'Erneut starten',
    contributorRunning: 'Bewertung läuft …',
    contributorRan: 'Die KI hat {n} Zellen bewertet.',
    contributorNotScored: 'Die KI hat diese Evaluierung noch nicht bewertet, daher kann ihr Einbeziehen das Ranking nicht verändern. Starten Sie sie zuerst.',
    contributorNotScoredLocked: 'Die Bewertung wurde gesperrt, bevor die KI bewertet hat, und sie kann nicht mehr gestartet werden — das Einbeziehen würde also nichts ändern.',
    contributorScored: '{n} von {total} Zellen wurden am {date} von der KI anhand der Anbieterunterlagen bewertet.',
    contributorUnrecorded: 'Diese Karte enthält {n} Bewertungen, doch wie sie entstanden sind, wurde nicht festgehalten; sie lassen sich daher nicht als Ergebnis einer KI-Auswertung belegen. Beziehen Sie sie nur ein, wenn Sie ihre Herkunft kennen.',
    analysisNone: 'Es gibt noch keine KI-Analyse zum Beifügen. Starten Sie zuerst die unabhängige Durchsicht.',
    analysisBuiltIn: 'Die gespeicherte unabhängige Durchsicht wurde mit integrierten Regeln erstellt, nicht von der KI; es gibt also keine KI-Analyse zum Beifügen. Starten Sie sie erneut.',
    analysisNeedsLock: 'Der Anhang lässt sich beifügen, sobald die Bewertung gesperrt ist.',
    builtInReport: 'Diese unabhängige Durchsicht wurde mit integrierten Regeln erstellt, nicht von der KI. Sie bleibt, wie aufgezeichnet, erhalten; starten Sie sie erneut, um sie durch eine KI-Auswertung zu ersetzen.',
    builtInBadge: 'Integrierte Regeln',
  },

  // Warum ein KI-Lauf nicht stattgefunden hat. Ein Satz je Ursache, jeweils mit dem nächsten Schritt.
  aiErrors: {
    notConfigured: 'Die KI ist auf dieser Installation nicht eingerichtet, daher wurde nichts analysiert. Bitten Sie die Betreiber:innen dieser Installation, sie anzubinden.',
    allowanceExhausted: 'Dieser Arbeitsbereich hat sein KI-Kontingent aufgebraucht, daher wurde nichts analysiert. Fordern Sie in den Einstellungen weitere Guthaben an oder warten Sie, bis das Kontingent zurückgesetzt wird.',
    noDocuments: 'Es gibt noch keine Anbieterunterlagen zum Lesen. Laden Sie zuerst die Angebote der Anbieter hoch und starten Sie dann erneut.',
    timeout: 'Die KI hat zu lange für die Antwort gebraucht, daher wurde nichts analysiert und nichts verändert. Versuchen Sie es erneut; weniger oder kürzere Unterlagen helfen.',
    failed: 'Die KI konnte die Analyse nicht abschließen, daher wurde nichts analysiert und nichts verändert. Versuchen Sie es gleich noch einmal.',
    locked: 'Die Bewertung ist gesperrt, die KI kann diese Evaluierung daher nicht mehr bewerten.',
    notInPlan: 'Die KI gehört nicht zum Tarif dieses Arbeitsbereichs. Fragen Sie Ihre Administration nach einer Erweiterung.',
    lapsed: 'Die KI-Testphase oder das KI-Abonnement dieses Arbeitsbereichs ist abgelaufen. Bitten Sie Ihre Administration, es zu verlängern.',
    switchedOff: 'Die KI-Werkzeuge sind für diesen Arbeitsbereich abgeschaltet. Wenden Sie sich an Ihre Administration.',
    alreadyRunning: 'Für diese Evaluierung läuft bereits eine Analyse. Warten Sie, bis sie fertig ist.',
  },

  matrix: {
    title: 'Compliance-Matrix',
    lead: 'Anforderung je Anbieter. Die Antworten stammen aus dem Anbieterportal und aus den hochgeladenen Angeboten.',
    refresh: 'Matrix aktualisieren',
    full: 'Erfüllt',
    partial: 'Teilweise',
    no: 'Nein',
    future: 'Geplant',
    unanswered: 'Offen',
    flags: 'Auffälligkeiten',
    noFlags: 'Keine Auffälligkeiten.',
    flagYesWithoutExplanation: 'Zusage ohne Erläuterung',
    flagUnanswered: 'Nicht beantwortet',
    flagFutureAsFull: 'Roadmap als verfügbar dargestellt',
    flagAllVendorsNo: 'Kein Anbieter erfüllt diese Muss-Anforderung',
    flagIrrelevantDocument: 'Dokument passt nicht zu dieser Bewertung',
    empty: 'Noch nichts zu vergleichen.',
    emptyLead: 'Senden Sie den Anbietern einen Link oder laden Sie deren Angebote unter Dokumente hoch.',
    onlyMust: 'Nur Muss-Anforderungen',
    filterVendor: 'Anbieter',
    matchSemantic: 'Aus Fließtext abgeleitet',
    boilerplateTitle: 'Wiederholte Antworten',
    boilerplateVendor: '{vendor} — {pct}% der Antworten wiederholt',
    boilerplateGroup: '{n} Anforderungen: {ids}',
    boilerplateEmpty: 'Keine wiederholten Antworten.',
    flagMore: '… und {n} weitere',
    flagShowAll: 'Alle {n} anzeigen',
    flagShowFewer: 'Weniger anzeigen',
    flagGoTo: '{id} in der Matrix anzeigen',
    flagWholeVendor: '{vendor} — alle {n} Anforderungen',
    aboutWhat: 'Eine Zeile je freigegebener Anforderung, eine Spalte je Anbieter — so sehen Sie auf einen Blick, wer was beantwortet hat und wo niemand geantwortet hat.',
    aboutHow: 'Jede Zelle füllt sich selbstständig aus den Antworten, die der Anbieter über seinen Portal-Link eingereicht hat, und aus den Angeboten, die Sie unter Dokumente hochgeladen haben — Portalantworten haben Vorrang. Eine Antwort wird einer Anforderung über die vom Anbieter genannte Anforderungs-ID zugeordnet; fehlt sie, über mindestens zwei Begriffe, die Antwort und Anforderung gemeinsam haben. Feste Regeln, kein KI-Modell: dieselben Einreichungen ergeben immer dieselbe Matrix.',
    aboutWeaker: 'Eine Zelle, die über gemeinsame Begriffe statt über die ID zugeordnet wurde, trägt das Kennzeichen „{badge}“. Das ist ein schwächeres Signal — lesen Sie das Zitat, bevor Sie sich darauf verlassen.',
  },

  clarify: {
    title: 'Nachfassen & Demo',
    lead: 'Punkte zum Nachfassen aus den Auffälligkeiten der Matrix und das Skript für das, was live gezeigt werden muss.',
    draftQuestions: 'Nachfass-Punkte erzeugen',
    buildScript: 'Demo-Skript erstellen',
    copyQuestions: 'Nachfass-Punkte kopieren',
    questions: 'Nachzufassen bei Anbietern',
    demoScript: 'Demo-Skript',
    demonstrated: 'Gezeigt',
    noQuestions: 'Noch keine Punkte zum Nachfassen. Aktualisieren Sie zuerst die Matrix — sie entstehen aus ihren Auffälligkeiten.',
    noScript: 'Noch kein Demo-Skript.',
  },

  tco: {
    title: 'Kommerziell',
    lead: 'Aus den kommerziellen Angeboten gelesene Zahlen. Nie von der KI bewertet und nur für den Eigentümer sichtbar.',
    extract: 'Zahlen extrahieren',
    category: 'Kategorie',
    amount: 'Betrag',
    missing: 'Fehlt in diesem Angebot',
    noFigures: 'Noch keine Zahlen extrahiert.',
    total: 'Summe der gelisteten Positionen',
    totalHint: 'Nur die Positionen oben — kein vollständiges TCO-Modell.',
    empty: 'Laden Sie die kommerziellen Angebote unter Dokumente hoch und extrahieren Sie dann die Zahlen.',
  },

  scoring: {
    title: 'Bewertung',
    lead: 'Jede:r Mitwirkende bewertet jeden Anbieter in jedem Kriterium.',
    myCard: 'Bewertungskarte: {name}',
    myCardLead: 'Bewerten Sie und notieren Sie den Nachweis. Änderungen werden beim Tippen gespeichert.',
    progress: 'Karte ausgefüllt',
    locked: 'Diese Bewertung ist gesperrt. Die Noten sind schreibgeschützt.',
    comment: 'Nachweisnotiz',
    commentPlaceholder: 'Was haben Sie gesehen, das diese Note stützt?',
    coverageTitle: 'Fortschritt je Mitwirkende',
    coverageLead: 'Fremde Bewertungen bleiben verborgen, damit die Urteile unabhängig bleiben.',
    seatState: 'Stand',
    notStarted: 'Nicht begonnen',
    inProgress: 'In Arbeit',
    inProgressCount: '{scored} von {total} bewertet',
    complete: 'Vollständig',
    noSeats: 'Noch keine Karten — erzeugen Sie zuerst PINs.',
    claimOwn: 'Übernehmen Sie eine Karte, wenn Sie selbst bewerten.',
    ratingLabel: 'Bewertung {min} bis {max}',
    criterionPosition: 'Kriterium {n} von {total}',
    ratedCount: '{filled} von {total} bewertet',
    nextCriterion: 'Nächstes Kriterium',
    addNote: 'Notiz hinzufügen',
    keyRate: 'bewerten',
    keyNextVendor: 'nächster Anbieter',
    keyNextCriterion: 'nächstes Kriterium',
    noCriteria: 'Noch keine Kriterien zu bewerten.',
    finish: 'Abschließen',
    finishReviewTitle: '{n} von {total} Bewertungen fehlen noch',
    finishMore: 'und {n} weitere',
    finishRateMissing: 'Fehlende bewerten',
    finishAnyway: 'Ohne sie abschließen',
    finishDoneTitle: 'Vielen Dank. Ihre Bewertung ist übermittelt.',
    finishDoneAll: 'Alle {total} Bewertungen sind erfasst.',
    finishDoneSome: '{filled} von {total} Bewertungen sind erfasst; {missing} blieben unbewertet.',
    finishReview: 'Meine Bewertungen ansehen',
    finishHome: 'Zurück zu meinen Bewertungen',
    finishResults: 'Zurück zu den Ergebnissen',
  },

  decision: {
    title: 'Entscheidung',
    lead: 'Dokumentiert wird das gewichtete Mittel der Mitwirkenden. Die KI-Lesart ist beratend und zeigt stets ihre Nachweise.',
    officialRanking: 'Dokumentiertes Ranking',
    officialHint: 'Gewichtetes Mittel der Mitwirkenden — das ist die maßgebliche Zahl.',
    topVendor: 'Führender Anbieter',
    margin: 'Abstand zum Zweiten',
    coverage: 'Abdeckung',
    cards: 'Bewertungskarten',
    sectionComparison: 'Vergleich je Abschnitt',
    compareCta: 'Mit und ohne KI vergleichen',
    compareTitle: 'Das Ranking mit und ohne KI',
    compareVendor: 'Anbieter',
    compareWithout: 'Ohne KI',
    compareWith: 'Mit KI',
    compareDifference: 'Unterschied',
    compareRank: 'Rang {n}',
    compareCurrent: 'Zählt aktuell',
    compareMovedUp: '{n} nach oben',
    compareMovedDown: '{n} nach unten',
    compareSameRank: 'Gleicher Rang',
    compareWinnerChanged: 'Mit der KI wechselt der Gewinner: Ohne sie führt {without}, mit ihr führt {with}.',
    compareOrderChanged: 'Mit der KI ändert sich die Reihenfolge der Anbieter. {winner} führt in beiden Fällen.',
    compareScoresChanged: 'Mit der KI ändern sich die Punktzahlen, nicht aber die Reihenfolge. {winner} führt in beiden Fällen.',
    compareNothing: 'Die KI ändert nichts: gleiche Reihenfolge und gleiche Punktzahlen.',
    compareNoAi: 'Die KI hat diese Evaluierung noch nicht bewertet, daher ändert ihr Einbeziehen nichts und es gibt nichts zu vergleichen.',
    compareUnrecorded: 'Wie die Bewertungen der KI entstanden sind, wurde nicht festgehalten; sie lassen sich daher nicht als Ergebnis einer KI-Auswertung belegen.',
    compareNoScores: 'Noch hat niemand bewertet, daher gibt es nichts zu vergleichen.',
    compareSections: 'Punktänderung je Abschnitt',
    heatmap: 'Heatmap der Abschnitte',
    statusTitle: 'Status',
    statusLead: 'Entwurf → Bewertung → gesperrt → entschieden. Sperren macht Noten schreibgeschützt, entschieden ist endgültig.',
    toScoring: 'Zur Bewertung freigeben',
    lock: 'Bewertung sperren',
    reopen: 'Zurück zur Bewertung',
    markDecided: 'Als entschieden markieren',
    lockConfirm: 'Diese Bewertung sperren? Die Noten werden schreibgeschützt. Dazu braucht es mindestens eine beigetretene Mitwirkende bzw. einen beigetretenen Mitwirkenden und mindestens eine Bewertung. Liegt die Abdeckung unter dem Minimum, müssen Sie das vorher bestätigen.',
    decideConfirm: 'Diese Entscheidung dokumentieren? Die Bewertung wechselt zu „entschieden“; der Vorgang wird mit Person und Zeitpunkt im Audit-Trail festgehalten. Es gelten dieselben Prüfungen wie beim Sperren, einschließlich der Bestätigung bei zu geringer Abdeckung. Eine Unterschrift ist ein eigener Schritt („{sign}“). Eine entschiedene Bewertung lässt sich wieder öffnen, aber nur mit schriftlicher Begründung — das macht alle Unterschriften ungültig.',
    signRecords: 'Mit der Unterschrift werden Ihr Name, der Zeitpunkt, die Bedeutung Ihrer Unterschrift und ein Fingerabdruck der Bewertung in ihrem jetzigen Stand festgehalten. Ändert sich die Bewertung danach oder wird sie wieder geöffnet, deckt die Unterschrift sie nicht mehr ab und wird entsprechend gekennzeichnet.',
    packTitle: 'Entscheidungspaket',
    packLead: 'Ein Dokument: Ranking, Gewichte, Nachweise, Abweichungen, Unterschriften und Audit-Trail.',
    packOpen: 'Entscheidungspaket öffnen',
    signTitle: 'Freigabe',
    signLead: 'Halten Sie fest, wer für diese Entscheidung einsteht und in welcher Rolle.',
    signCta: 'Entscheidung unterzeichnen',
    signMeaning: 'Ich unterzeichne als',
    signAuthored: 'Erstellung — ich habe diese Bewertung vorbereitet',
    signReviewed: 'Prüfung — ich habe sie geprüft',
    signApproved: 'Freigabe — ich genehmige dieses Ergebnis',
    signReason: 'Bedeutung Ihrer Unterschrift',
    signReasonPlaceholder: 'Freigegeben im Namen der Qualitätssicherung.',
    signPassword: 'Mit Ihrem Passwort bestätigen',
    signPasswordHint: 'Eine Unterschrift ist eine Handlung: Ihr Passwort bestätigt, dass Sie es sind.',
    signed: 'Unterzeichnet',
    signedBy: '{name} · {meaning}',
    noSignatures: 'Noch keine Unterschriften.',
    signNote: 'Nachweis zur Entscheidungsunterstützung. Dies ist kein validiertes Part-11-Signatursystem.',
    sealValid: 'Deckt den aktuellen Inhalt ab',
    sealInvalid: 'Die Bewertung hat sich seit der Unterschrift geändert — eine erneute Freigabe ist nötig.',
    sealSuperseded: 'Durch eine Wiedereröffnung ersetzt — diese Unterschrift gilt nicht mehr.',
    sealHash: 'Inhalt {hash}',
    completeness: '{pct}% vollständig',
    missingSections: 'Fehlt: {sections}',
    stakeholderMargin: 'Abstand im Mitwirkenden-Ranking',
    blendedMargin: 'Abstand im gemischten Ergebnis',
    reopenTitle: 'Grund für die Wiedereröffnung',
    reopenLead: 'Die Wiedereröffnung setzt diese Bewertung auf {status} zurück und ersetzt jede bestehende Unterschrift.',
    reopenReason: 'Grund',
    reopenReasonPlaceholder: 'Warum wird wiedereröffnet?',
    reopenCta: 'Wiedereröffnen',
    lowCoverageCta: 'Trotzdem dokumentieren',
    expertTitle: 'Eine zweite Meinung?',
    expertLead:
      'HLSC-Beraterinnen und -Berater prüfen Bewertung, Nachweise und Entscheidungspaket und kommen auf Wunsch in die Entscheidungsrunde.',
    expertCta: 'HLSC-Expertenprüfung anfragen',
    expertScope: 'Worauf sollen wir schauen?',
    expertMessage: 'Was sollten wir wissen?',
    expertSent: 'Anfrage gesendet. HLSC meldet sich per E-Mail.',
    noScores: 'Noch keine Bewertungen — erzeugen Sie PINs unter Mitwirkende.',
  },

  team: {
    title: 'Team & Lizenzen',
    lead: 'Wer in diesem Arbeitsbereich ist, was er darf und wie viele Plätze frei sind.',
    evaluators: 'Eigentümer',
    contributors: 'Contributors',
    evaluatorHint: 'Bewertungen anlegen, PINs erzeugen, Entscheidungen dokumentieren.',
    contributorHint: 'Bewerten nur die eigene Karte.',
    inviteTitle: 'Kolleg:in einladen',
    inviteLead: 'Sie erhalten einen Link, vergeben ein eigenes Passwort und landen in diesem Arbeitsbereich.',
    inviteCta: 'Einladung erstellen',
    invitePending: 'Offene Einladungen',
    inviteLink: 'Einladungslink',
    inviteLinkHint: 'Senden Sie den Link selbst — es ist kein Mailserver eingerichtet.',
    inviteEmailed: 'Die Einladung wurde an {email} gesendet.',
    inviteRevoke: 'Zurückziehen',
    members: 'Personen',
    plan: 'Lizenz',
    makeEvaluator: 'Zu Eigentümer machen',
    makeContributor: 'Zu Contributor machen',
    removeLicence: 'Lizenz entziehen',
    resetPassword: 'Reset-Link senden',
    resetLinkReady: 'Passwort-Reset-Link für {email}',
    noMembers: 'Bisher nur Sie.',
    seatsLeft: 'noch {n}',
    demoBlocked: 'Einladungen sind im Demo-Arbeitsbereich deaktiviert.',
  },

  instanceAdmin: {
    title: 'Instanzadministration',
    evaluatorSeats: 'Eigentümer-Plätze',
    contributorSeats: 'Contributor-Plätze',
    pendingInvites: 'Offene Einladungen',
    plan: 'Plan',
    mail: 'E-Mail',
    mailConfigured: 'Eingerichtet ({transport})',
    mailNotConfigured: 'Nicht eingerichtet — Links werden stattdessen angezeigt',
    people: 'Personen',
    invitations: 'Einladungen',
    role: 'Rolle',
    roleChanged: 'Rolle geändert',
    resetPassword: 'Reset-Link senden',
    resetLinkReady: 'Passwort-Reset-Link für {email}',
    signOut: 'Überall abmelden',
    remove: 'Entfernen',
    withdraw: 'Zurückziehen',
    noPeople: 'Bisher nur Sie.',
    noInvitations: 'Keine offenen Einladungen.',
    creditRequests: 'Kapazitätsanfragen',
    grantAmount: 'Menge',
    grant: 'Gewähren',
    dismiss: 'Verwerfen',
    creditGranted: 'Guthaben gewährt',
    creditRequestDismissed: 'Anfrage verworfen',
    noCreditRequests: 'Keine offenen Anfragen.',
  },

  consoles: {
    lockPolicyTitle: 'Wer die Bewertung abschließen darf',
    lockPolicyLead: 'Der Abschluss ist unwiderruflich und macht die Bewertungen aller Mitwirkenden sichtbar.',
    lockPolicyOwnerOnly: 'Nur die Eigentümer:in der Evaluierung',
    lockPolicyAnyEvaluator: 'Jede:r Eigentümer:in der Evaluierung',
    lockPolicySaved: 'Gespeichert',
    defaultAiTitle: 'Standard-KI-Einstellung für neue Evaluierungen',
    defaultAiLead: 'Die Ausgangsstellung für den KI-Schalter einer neuen Evaluierung. Beim Anlegen kann sie noch geändert werden.',
    defaultAiOn: 'An',
    defaultAiOff: 'Aus',
    defaultAiSaved: 'Gespeichert',
    documentTemplatesTitle: 'Vorlagen für Dokumentenanfragen',
    documentTemplatesLead: 'Ihre eigenen wiederverwendbaren Anfragelisten sowie die gemeinsame Bibliothek von HLSC unten.',
    documentTemplatesShared: 'Gemeinsame Bibliothek',
    documentTemplatesOwn: 'Ihr Arbeitsbereich',
    documentTemplatesSaveCopy: 'Kopie für Ihren Arbeitsbereich speichern',
    documentTemplatesCopySaved: 'Kopie in Ihrem Arbeitsbereich gespeichert',
    documentTemplatesName: 'Vorlagenname',
    documentTemplatesCreate: 'Als neue Vorlage speichern',
    documentTemplatesRemove: 'Entfernen',
    documentTemplatesEmpty: 'Noch nichts gespeichert.',
    criteriaTemplatesTitle: 'Kriterienvorlagen',
    criteriaTemplatesLead: 'Die eigenen gespeicherten Gruppen und Kriterien Ihres Arbeitsbereichs — für niemand anderen sichtbar.',
    criteriaTemplatesName: 'Vorlagenname',
    criteriaTemplatesCreate: 'Aktuelle als Vorlage speichern',
    criteriaTemplatesRemove: 'Entfernen',
    criteriaTemplatesEmpty: 'Noch nichts gespeichert.',
    lockedRegisterTitle: 'Gesperrte Evaluierungen',
    lockedRegisterLead: 'Evaluierungen in Ihrem Arbeitsbereich, deren Bewertung gesperrt ist — nur Metadaten.',
    lockedRegisterEvaluation: 'Evaluierung',
    lockedRegisterLockedBy: 'Gesperrt von',
    lockedRegisterLockedAt: 'Gesperrt am',
    lockedRegisterEmpty: 'Noch nichts gesperrt.',
  },

  share: {
    send: 'Per E-Mail senden',
    sendFallback: 'Nichts hat sich geöffnet? Dann ist auf diesem Gerät kein E-Mail-Programm eingerichtet — kopieren Sie stattdessen den Link.',
    hello: 'Guten Tag {name},',
    helloAnon: 'Guten Tag,',
    portalSubject: '{project}: Dokumente und Angaben von {vendor} angefordert',
    portalIntro: 'Wir vergleichen Anbieter für „{project}“ und möchten, dass {vendor} teilnimmt. Dies ist Ihr persönlicher Link:',
    portalAsk: 'Bitte öffnen Sie ihn, beantworten Sie die Anforderungen und laden Sie die angeforderten Dokumente hoch. Der Link gilt nur für {vendor} — bitte leiten Sie ihn nicht weiter.',
    portalDue: 'Bitte senden Sie Ihre Antworten bis zum {date}.',
    contributorSubject: '{project}: Ihre Bewertungskarte',
    contributorIntro: 'Sie sind gebeten, die Anbieter in „{project}“ zu bewerten. Dies ist Ihr persönlicher Link zu Ihrer privaten Bewertungskarte:',
    contributorAsk: 'Öffnen Sie ihn und bewerten Sie jeden Anbieter je Kriterium. Zusätzlich benötigen Sie einen Zugangscode, den Sie getrennt erhalten.',
    rankingSubject: '{project}: Anbieter-Ranking',
    rankingIntro: 'Hier ist das dokumentierte Ranking für „{project}“, nur lesbar:',
    rankingAsk: 'Die Seite zeigt ausschließlich das Ranking.',
    inviteSubject: 'Ihre Einladung zu {workspace} in Hanseatic Select',
    inviteIntro: 'Sie wurden eingeladen, {workspace} beizutreten. Über Ihren persönlichen Link richten Sie Ihren Zugang ein:',
    inviteAsk: 'Der Link ist nur für Sie bestimmt — bitte leiten Sie ihn nicht weiter.',
    inviteWorkspaceFallback: 'unserem Arbeitsbereich',
    resetSubject: 'Passwort zurücksetzen für {workspace}',
    resetIntro: 'Für Ihr Konto in {workspace} wurde ein neues Passwort angefordert. Über Ihren persönlichen Link wählen Sie ein neues Passwort:',
    resetAsk: 'Falls Sie das nicht erwartet haben, informieren Sie Ihre Arbeitsbereichs-Verwaltung. Der Link ist nur für Sie bestimmt.',
    contactSubject: 'Anfrage zu Hanseatic Select',
    contactIntro: 'Ich möchte mit Ihnen über Hanseatic Select sprechen.',
    contactAsk: 'Name:\nOrganisation:\nMeine Rolle:\nWas wir auswählen:',
    contactFallback: 'Nichts hat sich geöffnet? Schreiben Sie an {email} aus Ihrem eigenen Mailprogramm.',
  },

  settings: {
    title: 'Einstellungen des Arbeitsbereichs',
    organisation: 'Organisation',
    plan: 'Tarif',
    planBasic: 'Basic',
    planAi: 'KI',
    planTrial: 'KI-Test',
    planBasicLead: 'Bewerten, Ranking und Entscheidungspaket, ohne KI-Werkzeuge.',
    planAiLead: 'Entwurf aus dem Lastenheft, unabhängige Auswertung, Matrix, Nachfass-Punkte und kommerzielle Auswertung.',
    trialEnds: 'Test endet am {date}',
    aiExpires: 'KI-Abonnement läuft bis {date}',
    upgradeCta: 'Mit HLSC über die KI-Verlängerung sprechen',
    dataTitle: 'Daten und KI-Verarbeitung',
    dataLead:
      'Für die unabhängige Auswertung wird Bewertungstext verarbeitet. Im gehosteten Dienst geht dieser Aufruf an einen externen Modellanbieter; eine private Instanz kann auf Ihr eigenes Modell zeigen.',
    licenceKey: 'Lizenzschlüssel der Organisation',
    activate: 'Aktivieren',
    navigation: 'Navigation',
    navSidebar: 'Seitenleiste',
    navBottomBar: 'Fußleiste',
    aiUsageTitle: 'KI-Nutzung',
    aiUsageFeature: 'Funktion',
    aiUsageUsed: 'Genutzt',
    aiUsageAllowance: 'Kontingent',
    aiUsageCredits: 'Guthaben',
    aiUsageResets: 'Zurückgesetzt am {date}',
    aiUsageRecent: 'Letzte Nutzung',
    aiKindEvaluation: 'Unabhängige Auswertung',
    aiKindDrafting: 'Entwurf aus dem Lastenheft',
    aiKindAssistant: 'Assistent',
    aiKindTender: 'Compliance-Matrix und Nachfass-Punkte',
    aiKindAny: 'Beliebige Funktion',
    aiRequestMore: 'Mehr anfragen',
    aiRequestTitle: 'Mehr KI-Guthaben anfragen',
    aiRequestKind: 'Funktion',
    aiRequestNote: 'Notiz',
    aiRequestSend: 'Anfrage senden',
    aiRequestSent: 'Anfrage gesendet',
    aiBuyCredits: 'Guthaben kaufen',
    aiBuyCreditsAskAdmin: 'Bitten Sie Ihre Instanzadministration, weiteres Guthaben zu kaufen',
    aiBuyCreditsTitle: 'KI-Guthaben kaufen',
    aiBuyPacks: 'Pakete zu 1.000 Guthabenpunkten',
    aiBuyPackPrice: '{eur} € pro Paket mit {size} Guthabenpunkten',
    aiBuySubmit: 'Weiter zur Zahlung',
    aiUsageOf: '{used} von {total}',
    aiUsageByFeature: 'Nutzung nach Funktion',
    aiUsageShareLabel: '{percent}% der Nutzung in diesem Zeitraum',
    aiUsageCreditBalance: 'Guthabenstand',
    aiUsageSharedCredit: 'Gemeinsames Guthaben, beliebige Funktion',
    aiUsageCreditRemaining: '{remaining} von {granted} verbleibend',
    aiUsageNoCredit: 'Kein Guthaben gewährt',
    aiUsageLastUpdated: 'Aktualisiert {time}',
    aiUsageRefresh: 'Aktualisieren',
    aiUsageProjectionWarning: 'Bei diesem Tempo wird das Kontingent für {feature} vor der Zurücksetzung am {date} aufgebraucht sein.',
    aiUsageOverallProjectionWarning: 'Bei diesem Tempo wird die KI-Nutzung vor der Zurücksetzung am {date} aufgebraucht sein.',
    aiUsagePeriodLabel: 'Dieser Zeitraum',
    aiUsageExtraCapacity: 'Zusätzliche Kapazität',
    aiUsageUnitsOf: '{used} von {total} KI-Einheiten',
  },

  portal: {
    title: 'Antwort auf die Anforderungsliste',
    lead: '{client} lädt {vendor} ein, die Anforderungsliste zu „{project}“ zu beantworten.',
    intro:
      'Beantworten Sie jede Anforderung, ergänzen Sie den Nachweis und laden Sie Ihr Angebot hoch. Bis zum Einreichen können Sie jederzeit zurückkehren.',
    compliance: 'Erfüllung',
    explanation: 'Erläuterung',
    reference: 'Fundstelle im Angebot',
    referencePlaceholder: 'Abschnitt 3.1',
    explanationPlaceholder: 'Wie erfüllen Sie diese Anforderung?',
    progress: '{answered} von {total} beantwortet',
    saveDraft: 'Antworten speichern',
    submit: 'Antwort einreichen',
    submitted: 'Eingereicht am {date}. Vielen Dank — das Bewertungsteam sieht Ihre Antworten.',
    submitConfirm: 'Antwort einreichen? Sie können den Link weiterhin öffnen und ergänzen.',
    dueAt: 'Antwort bis {date}',
    sourceTitle: 'Lastenheft',
    templateCta: 'Antwortvorlage herunterladen',
    uploadTitle: 'Ihre Dokumente',
    documentsTitle: 'Dokumente',
    questionsTitle: 'Fragen',
    extraUpload: 'Was Sie uns sonst noch senden möchten',
    filterMust: 'Muss',
    uploadLead: 'Technisches Angebot, kommerzielles Angebot, Rückfragen oder Präsentation.',
    uploaded: 'Hochgeladen',
    readOnly: 'Diese Bewertung ist abgeschlossen. Ihre Antworten sind schreibgeschützt.',
    readOnlySubmitted: 'Ihre Antwort wurde eingereicht und ist schreibgeschützt. Bitten Sie das Bewertungsteam, sie wiederzueröffnen, wenn Sie etwas ändern müssen.',
    expired: 'Dieser Link ist abgelaufen. Bitte fordern Sie einen neuen an.',
    mustAnswer: 'Beantworten Sie vor dem Einreichen alle Muss-Anforderungen.',
    privacy: 'Nur das Bewertungsteam sieht Ihre Antworten. Andere Anbieter nie.',
    requiredTitle: 'Erforderliche Dokumente',
    requiredProgress: '{done} von {total} erforderlichen Punkten erledigt',
    requiredMandatory: 'Pflicht',
    requiredOptional: 'Optional',
    requiredUpload: 'Hochladen',
    requiredReplace: 'Ersetzen',
    requiredNotUploaded: 'Noch nicht hochgeladen',
    requiredFormats: 'Akzeptiert: {formats}',
    mustUploadDocuments: 'Beantworten Sie vor dem Einreichen jedes Pflichtfeld.',
    choicePlaceholder: 'Bitte wählen',
    yes: 'Ja',
    no: 'Nein',
    requirementsTitle: 'Anforderungen',
    colId: 'ID',
    colRequirement: 'Anforderung',
    colStatus: 'Status',
  },

  contributor: {
    invalid: 'Bitten Sie Ihren Kontakt um einen neuen Link.',
    pinTitle: 'Zugangscode eingeben',
    pinLabel: 'Zugangscode',
    pinSubmit: 'Weiter',
    pinWrong: 'Dieser Code wurde nicht akzeptiert.',
    pinLimited: 'Zu viele Versuche. Warten Sie einige Minuten und versuchen Sie es erneut.',
    decisionPosition: 'Entscheidung {n} von {total}',
    skip: 'Vorerst überspringen',
    keyBack: 'zurück',
  },

  admin: {
    refresh: 'Aktualisieren',
    address: 'Adresse',
    addressPlaceholder: 'gemeinsam',
    addressSet: 'Adresse gespeichert — DNS auf diesen Server zeigen lassen',
    addressCleared: 'Adresse entfernt',
    customer: 'Kunde',
    noCustomer: 'Nicht zugeordnet',
    assigned: 'Workspace zugeordnet',
    unassigned: 'Zuordnung aufgehoben',
    title: 'Betreiberkonsole',
    signIn: 'Betreiber-Anmeldung',
    tokenLabel: 'Betreiber-Token',
    overview: 'Überblick',
    workspaces: 'Arbeitsbereiche',
    leads: 'Anfragen',
    expertReviews: 'Expertenprüfungen',
    aiCredits: 'Guthabenanfragen',
    totalWorkspaces: 'Arbeitsbereiche',
    aiWorkspaces: 'Mit KI',
    demoWorkspaces: 'Demo',
    openLeads: 'Offene Anfragen',
    openReviews: 'Offene Prüfanfragen',
    openCreditRequests: 'Offene Guthabenanfragen',
    signingWarning: 'Signaturschlüssel für Lizenzen',
    enableAi: 'KI aktivieren (1 Jahr)',
    setExpiry: 'Ablauf setzen',
    manage: 'Verwalten',
    suspend: 'Sperren',
    reactivate: 'Entsperren',
    lastActivity: 'Letzte Aktivität',
    aiRuns: 'KI-Läufe',
    seats: 'Plätze',
    markContacted: 'Als kontaktiert markieren',
    markClosed: 'Schließen',
    markScheduled: 'Als terminiert markieren',
    markGranted: 'Als gewährt markieren',
  },

  errors: {
    generic: 'Da ist etwas schiefgegangen.',
    notFound: 'Diese Seite gibt es nicht.',
    notFoundLead: 'Der Link ist vielleicht alt, oder die Bewertung wurde entfernt.',
    forbidden: 'Dafür fehlt Ihnen die Berechtigung.',
    offline: 'Der Server hat nicht geantwortet. Prüfen Sie die Verbindung und versuchen Sie es erneut.',
    sessionExpired: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
    backHome: 'Zurück zu Ihren Bewertungen',
    backSite: 'Zurück zur Startseite',
    draftFailed: 'Der Entwurf konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
    draftStreamUnreadable: 'Die Verbindung zur Entwurfs-Engine wurde unterbrochen. Bitte versuchen Sie es erneut.',
    draftIncomplete: 'Der Entwurf wurde beendet, ohne ein Ergebnis zu liefern. Bitte versuchen Sie es erneut.',
    decisionPackFailed: 'Das Entscheidungsdossier konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    reportDeckFailed: 'Die Übersichtspräsentation konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    responseTemplateFailed: 'Die Antwortvorlage konnte nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
    portalTemplateFailed: 'Die Antwortvorlage konnte nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
    sourceDocumentFailed: 'Das Dokument konnte nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
    scorecardCsvFailed: 'Die Bewertungsübersicht konnte nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
    rtmCsvFailed: 'Die Anforderungsmatrix konnte nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
  },

  embed: {
    evaluation: 'Lieferantenbewertung',
    coverage: '{coverage}% bewertet · {claimed} von {total} Karten',
    noScores: 'Noch keine Bewertungen.',
    asOf: 'Stand {time}',
    unavailable: 'Diese Ansicht ist nicht verfügbar.',
    stale: 'Seit dieser Ablage für Änderungen wiedereröffnet — die hier gezeigten Zahlen können veraltet sein.',
    shareTitle: 'Schreibgeschützte Ansicht teilen',
    shareLead: 'Ein Link, der nur das eingereichte Ranking zeigt — für eine SharePoint-Seite, ein Intranet oder eine Folie. Jederzeit widerrufbar.',
    shareCta: 'Ansichts-Link erstellen',
    shareLabel: 'Wo wird er eingebunden?',
    shareLabelPlaceholder: 'Programmseite in SharePoint',
    shareEmail: 'Senden an (optional)',
    shareEmailPlaceholder: 'sponsor@firma.de',
    shareIframe: 'Einbettungscode',
    shareLinkTitle: 'Link zur schreibgeschützten Ansicht',
    shareLinkHint: 'Wer den Link hat, sieht das Ranking. Er wird einmal angezeigt — gespeichert wird nur ein Hash.',
    shareRevoke: 'Widerrufen',
    shareEmpty: 'Noch keine Ansichts-Links.',
    shareViews: '{n} Aufrufe',
    shareNever: 'Noch nicht geöffnet',
    shareStatusActive: 'Aktiv',
    shareStatusRevoked: 'Widerrufen',
    shareStatusExpired: 'Abgelaufen',
    shareNeedsLocked: 'Veröffentlichen Sie eine dokumentierte Bewertung: zuerst sperren oder als entschieden markieren.',
    shareDemoBlocked: 'In einem Demo-Arbeitsbereich stehen keine schreibgeschützten Links zur Verfügung.',
    shareLife: 'Links bleiben 90 Tage gültig.',
  },

  teams: {
    tabName: 'Hanseatic Select',
    refresh: 'Aktualisieren',
    needsSignIn: 'Melden Sie sich in Ihrem Workspace an. Teams und Browser teilen dasselbe Konto — es ist dieselbe Bewertung.',
    signInCta: 'Mit E-Mail anmelden',
    signInCancelled: 'Die Anmeldung wurde abgebrochen.',
    ssoCta: 'Mit Microsoft fortfahren',
    signInLead: 'Melden Sie sich an, um diesen Workspace in Teams zu öffnen.',
    signInPrivacy: 'Ihr Passwort geht an Ihre eigene Instanz, nie an Microsoft.',
    configTitle: 'Welche Bewertung soll dieser Tab öffnen?',
    configLead: 'Alle, die den Tab öffnen, landen hier. Sie können das später im Tab-Menü ändern.',
    configAll: 'Alle Bewertungen',
    configAllHint: 'Den Workspace öffnen und dort auswählen.',
    pickProject: 'Bewertung auswählen',
    openFull: 'Bewertung öffnen',
    openBrowser: 'Im Browser öffnen',
    shareStage: 'Auf die Besprechungsbühne teilen',
    coverage: '{coverage}% bewertet · {claimed} von {total} Karten übernommen',
    outsideTeams: 'Diese Seite gehört zur Microsoft-Teams-App. Im Browser öffnen Sie Hanseatic Select bitte direkt.',
  },

  site: {
    nav: {
      product: 'Produkt',
      how: 'Ablauf',
      pricing: 'Preise',
      security: 'Sicherheit',
      contact: 'Kontakt',
      signIn: 'Anmelden',
      start: 'Zugang anfragen',
    },
    hero: {
      eyebrow: 'Software-Lieferantenauswahl auf Basis von Nachweisen',
      title: 'Wählen Sie Ihren Anbieter nach Nachweisen, nicht nach Meinungen.',
      lead: 'Hanseatic Select macht aus Ihrem Lastenheft gewichtete Kriterien, lässt jede Fachabteilung vertraulich bewerten und prüft jede Anbieteraussage an deren eigenen Unterlagen — damit die Empfehlung auf Nachweisen beruht und nicht auf der lautesten Stimme im Raum.',
      ctaPrimary: 'Zugang anfragen',
      ctaSecondary: 'Demo ansehen',
      note: 'Drei Minuten echtes Produkt.',
      shotCaption: 'Live-Produktansicht — die abgelegte Rangfolge, vom Nachweis zur Entscheidung.',
      badge1: 'Lastenheft rein, Kriterien raus',
      badge2: 'Jede Aussage mit Zitat belegt',
      badge3: 'Auditfestes Entscheidungspaket',
      highlight: 'Nachweisen',
      chip1: 'Zitat im Angebot bestätigt',
      chip2: 'Private Bewertungskarten',
      chip3: 'gewichteter Mitwirkenden-Mittelwert',
    },
    instrument: {
      title: 'Live-Ranking',
      subtitle: 'Gewichtetes Mittel · 1–5',
      vendorA: 'Anbieter A',
      vendorB: 'Anbieter B',
      vendorC: 'Anbieter C',
      sectionIntegrity: 'Datenintegrität & Annex 11',
      sectionFit: 'Funktionale Passung',
      sectionValidation: 'Validierung & Support',
      sectionCommercial: 'Kommerzielles & Stabilität',
      evidenceQuote: 'Der Audit-Trail zeichnet Person, Zeitstempel, alten Wert, neuen Wert und Grund für jede GxP-Datenänderung auf.',
      evidenceSource: 'Bestätigt · Technisches Angebot, §4.2',
      callout1Label: '01',
      callout1: 'Ziehen Sie ein beliebiges Gewicht. Das Ranking rechnet mit derselben Arithmetik neu, die das dokumentierte Entscheidungspaket verwendet.',
      callout2Label: '02',
      callout2: 'Gelb markiert eine Aussage, die bis zu einer Zeile im eigenen Dokument des Anbieters zurückverfolgt ist — nie eine Meinung.',
      verdictRobust: '{winner} gewinnt bei jeder Gewichtung.',
      verdictFragile: '{winner} gewinnt — außer {section} bewegt sich auf {percent}%, dann führt {vendor}.',
      verdictSectionRobust: '{winner} führt bei jeder Gewichtung von {section}.',
      verdictFlipDown: '{vendor} übernimmt die Führung, wenn {section} auf {percent}% sinkt.',
      verdictFlipUp: '{vendor} übernimmt die Führung, wenn {section} auf {percent}% steigt.',
    },
    problem: {
      title: 'Lieferantenauswahl scheitert fast immer an denselben vier Stellen',
      p1Title: 'Die Kriterien kommen nach den Demos',
      p1: 'Alle haben sich längst eine Meinung gebildet, und die Bewertungsmatrix wird passend dazu geschrieben.',
      p2Title: 'Eine laute Stimme gibt den Ton an',
      p2: 'Die Noten wandern zu der Person, die am meisten redet — nicht zu den Nachweisen.',
      p3Title: '„Voll erfüllt“ wird geglaubt',
      p3: 'Roadmap-Themen werden als fertige Funktionen verkauft, und niemand prüft die Formulierung.',
      p4Title: 'Die Begründung lässt sich nicht rekonstruieren',
      p4: 'Ein halbes Jahr später, im Audit, kann niemand sagen, warum dieser Anbieter gewonnen hat.',
    },
    how: {
      title: 'Fünf Schritte, eine belastbare Entscheidung',
      lead: 'Der Ablauf, den eine HLSC-Beraterin im Auswahlworkshop nutzt — jetzt als Software für Ihr Team.',
      s1Title: 'Mit dem Lastenheft starten',
      s1: 'Lastenheft und Ausschreibung hochladen. Wir machen daraus einen gewichteten Kriterienbaum, den Sie freigeben.',
      s2Title: 'Gewichten, was zählt',
      s2: 'Abschnitte und Kriterien erhalten relative Gewichte — vereinbart, bevor jemand eine Demo sieht.',
      s3Title: 'Unabhängig bewerten',
      s3: 'Jede:r Mitwirkende bewertet auf einer eigenen Karte mit Stimmgewicht — niemand sieht fremde Noten.',
      s4Title: 'Aussagen prüfen',
      s4: 'Anbieter antworten über einen privaten Link. Wir prüfen jede Aussage gegen das Angebot und markieren Roadmap-Themen, die als verfügbar verkauft werden.',
      s5Title: 'Entscheidung dokumentieren',
      s5: 'Ein Paket: gewichtetes Ranking, Unterschriften mit ihrer Bedeutung, markierte Abweichungen und der vollständige Audit-Trail.',
    },
    showcase: {
      title: 'So sieht es in der Praxis aus',
      m1Title: 'Jede Anforderung, jeder Anbieter, nebeneinander',
      m1: 'Jede Antwort tr\u00e4gt die Formulierung des Anbieters und den Absatz, aus dem sie stammt. Unbeantwortet bleibt unbeantwortet \u2014 es wird nie weggemittelt.',
      m2Title: 'Eine unabh\u00e4ngige Pr\u00fcfung der Angebote',
      m2: 'Die Aussagen werden gegen die Dokumente gepr\u00fcft, Roadmap-Funktionen von verf\u00fcgbaren getrennt und die Bewertung auf Muster gepr\u00fcft, die ein Ergebnis verzerren.',
    },
    features: {
      title: 'Das bekommen Sie',
      f1Title: 'Gewichtete Bewertung, die standhält',
      f1: 'Abschnitts- und Kriteriengewichte, Stimmgewichte je Mitwirkende und ein Ranking, das sich mit jeder Note aktualisiert.',
      f2Title: 'Nachweise statt Bauchgefühl',
      f2: 'Jede unabhängige Note verweist auf ein wörtliches Zitat im Angebot — alles andere fliegt raus.',
      f3Title: 'Anbieterportal',
      f3: 'Anbieter antworten und laden Angebote über einen privaten Link hoch. Keine Konten, keine Postfach-Suche.',
      f4Title: 'Integritätsprüfung',
      f4: 'Statistische Prüfungen markieren Karten, die den Nachweisen widersprechen, und senken deren Gewicht in der beratenden Lesart.',
      f5Title: 'Entscheidungspaket',
      f5: 'Ein auditfestes Dokument: Ranking, Methode, Gewichte, Zitate, Lücken, Unterschriften und Audit-Trail.',
      f6Title: 'Teams und eigener Betrieb',
      f6: 'Nutzen Sie den gehosteten Arbeitsbereich, binden Sie ihn als Teams-Tab ein, oder betreiben Sie alles im eigenen Docker-Netz.',
    },
    honesty: {
      title: 'Was die KI tut — und was nie',
      doesTitle: 'Sie tut',
      does1: 'Jedes Angebot gegen jedes Kriterium lesen und die herangezogene Stelle zitieren.',
      does2: 'Jede Aussage als bestätigend, widersprechend, gemischt oder fehlend kennzeichnen, inklusive Verneinungen.',
      does3: 'Roadmap-Sprache markieren, die als verfügbare Funktion auftritt.',
      does4: 'Zeigen, wo Gremium und Nachweise auseinandergehen.',
      notTitle: 'Sie tut nie',
      not1: 'Die dokumentierte Note schreiben. Das ist immer das Mittel der Mitwirkenden.',
      not2: 'Zitate erfinden. Was nicht wörtlich im Dokument steht, wird verworfen.',
      not3: 'Ein fremdes Dokument als Treffer werten — die Abdeckung fällt auf null und das Dokument wird markiert.',
      not4: 'Ihren Beschaffungs- oder Qualitätsprozess ersetzen. Das ist Entscheidungsunterstützung, kein validiertes System — Organisationen unter GxP sollten es weiterhin außerhalb ihrer validierten Systemgrenze behandeln.',
    },
    audience: {
      title: 'Für die Menschen im Raum gebaut',
      a1Title: 'Projektleitung',
      a1: 'Die Auswahl führen, ohne noch eine Tabelle zu bauen.',
      a2Title: 'Qualität',
      a2: 'Eine Begründung, die ein Audit übersteht — mit den Nachweisen daneben.',
      a3Title: 'IT und Automatisierung',
      a3: 'Integrations- und Architekturkriterien richtig gewichtet, nicht als Anhängsel.',
      a4Title: 'Geschäftsführung',
      a4: 'Eine Seite, die zeigt, was entschieden wurde, von wem und auf welcher Grundlage.',
    },
    cta: {
      title: 'Führen Sie die nächste Auswahl über Nachweise',
      lead: 'In zwei Minuten kostenlos starten — oder sich erst eine fertige Bewertung ansehen.',
      primary: 'Kostenlos starten',
      secondary: 'Demo ansehen',
      tertiary: 'Mit HLSC sprechen',
    },
    pricing: {
      title: 'Preise',
      lead: 'Alles {days} Tage kostenlos testen. Danach ein Plan, zugeschnitten auf Ihre Organisation.',
      recommended: 'Hier starten',
      perMonth: 'pro Arbeitsbereich / Monat, zzgl. MwSt.',
      fromPrefix: 'Ab',
      trialName: 'Kostenloser Test',
      trialPrice: '{days} Tage kostenlos',
      trialNote: 'Keine Kreditkarte. Alles inklusive.',
      trialLead: 'Das ganze Produkt, KI-Werkzeuge inklusive, in einem gehosteten Arbeitsbereich.',
      trialF1: 'Unbegrenzt Bewertungen und Anbieter',
      trialF2: 'Kriterienentwurf aus Ihrem Lastenheft',
      trialF3: 'Unabhängige Auswertung mit Zitatbelegen',
      trialF4: 'Compliance-Matrix und Anbieterportal',
      trialF5: 'Ranking, Freigabe und Entscheidungspaket',
      trialF6: '3 Eigentümer, 25 Contributors',
      teamName: 'Team',
      teamLead: 'Eine fokussierte Lieferantenauswahl, vom Start bis zum Entscheidungspaket.',
      teamF1: '3 Eigentümer, 25 Contributors',
      teamF2: '25 unabhängige Auswertungen pro Monat',
      teamF3: '25 Entwürfe aus einem Lastenheft pro Monat',
      teamF4: '1.000 Assistenzfragen pro Monat',
      teamF5: '50 Ausschreibungs-Aktualisierungen pro Monat',
      teamF6: 'Zusatzguthaben: 49 € je 1.000 Credits',
      businessName: 'Business',
      businessLead: 'Mehrere Auswahlverfahren gleichzeitig, über Teams hinweg.',
      businessF1: '10 Eigentümer, 100 Contributors',
      businessF2: '75 unabhängige Auswertungen pro Monat',
      businessF3: '75 Entwürfe aus einem Lastenheft pro Monat',
      businessF4: '3.000 Assistenzfragen pro Monat',
      businessF5: '150 Ausschreibungs-Aktualisierungen pro Monat',
      businessF6: 'Zusatzguthaben: 49 € je 1.000 Credits',
      enterpriseName: 'Enterprise',
      enterpriseLead: 'Ihre eigene Instanz, dimensioniert und betreut für Skalierung.',
      enterpriseF1: 'Unbegrenzt Eigentümer und Contributors',
      enterpriseF2: 'Eigene Instanz oder On-Premise',
      enterpriseF3: 'Microsoft Teams und Single Sign-on mit Entra',
      enterpriseF4: 'Bevorzugter Support',
      enterpriseF5: 'Individuelles monatliches Kontingent',
      enterpriseF6: 'HLSC-Expertenprüfung Ihrer Auswahlverfahren',
      talk: 'Mit HLSC sprechen',
      mailSubject: 'Hanseatic Select — Enterprise',
      faqTitle: 'Fragen, die vor dem Kauf kommen',
      q1: 'Was passiert, wenn der Test endet?',
      a1: 'Die KI-Werkzeuge schalten sich ab; Bewertungen, Noten und Entscheidungspakete bleiben erhalten. Sprechen Sie uns auf einen Plan an, um mit allem weiterzuarbeiten.',
      q2: 'Wo liegen unsere Daten?',
      a2: 'In dem Arbeitsbereich, für den Sie sich registriert haben — oder in Ihrer eigenen Instanz, wenn Sie selbst betreiben. Anbieterdokumente werden als Text für die Bewertung gespeichert, und nichts wird zwischen Organisationen geteilt.',
      q3: 'Ist das ein validiertes GxP-System?',
      a3: 'Nein, und wir behaupten es nicht. Es ist Entscheidungsunterstützung für jede Software-Lieferantenauswahl: Es liefert Nachweise und Begründung, den Rest übernimmt Ihr eigener Prozess — Qualität, Beschaffung oder anderweitig.',
      q4: 'Können wir es im eigenen Netz betreiben?',
      a4: 'Ja. Enterprise kann eine eigene Instanz oder eine Installation auf Ihren Servern sein, auf Wunsch mit einem selbst betriebenen Modell.',
    },
    security: {
      title: 'Sicherheit und Compliance',
      lead: 'Was das Produkt mit Ihrer Bewertung macht, in klaren Worten.',
      accessTitle: 'Zugriff und Trennung',
      access1: 'Jede Anfrage wird gegen die Organisation geprüft, der die Bewertung gehört.',
      access2: 'Contributors sehen ausschließlich ihre eigene Bewertungskarte.',
      access3: 'Anbieter-Links gelten für genau einen Anbieter, laufen ab und lassen sich jederzeit zurückziehen.',
      access4: 'Passwörter werden als gesalzene scrypt-Hashes gespeichert, PINs ebenso.',
      dataTitle: 'Ihre Daten',
      data1: 'Dokumente werden als extrahierter Text in Ihrem Arbeitsbereich gespeichert.',
      data2: 'Nichts wird zwischen Organisationen geteilt, und nichts trainiert ein Modell.',
      data3: 'Demo-Arbeitsbereiche werden automatisch gelöscht, samt allen Inhalten.',
      data4: 'Entscheidungspaket und CSV-Export sorgen dafür, dass Ihre Unterlagen nie eingeschlossen sind.',
      aiTitle: 'KI-Verarbeitung',
      ai1: 'Die unabhängige Auswertung sendet Bewertungstext an einen Modellanbieter, sofern die KI-Werkzeuge aktiv sind.',
      ai2: 'In einer privaten Instanz wählen Sie den Endpunkt — auch ein Modell auf eigener Hardware.',
      ai3: 'Ohne KI laufen die lexikalische und die sprachliche Auswertung weiterhin lokal.',
      trailTitle: 'Audit-Trail',
      trail1: 'Jede Änderung wird mit Person, Aktion und Zeitstempel aufgezeichnet.',
      trail2: 'Freigaben halten Unterzeichner:in, Bedeutung und Zeitpunkt fest.',
      trail3: 'Sperren macht Noten schreibgeschützt; entschieden ist endgültig.',
      contactTitle: 'Sicherheitslücke melden',
      contactLead: 'Ein Sicherheitsproblem gefunden? Schreiben Sie an {email}, wir melden uns.',
      note: 'Hanseatic Select ist ein Werkzeug zur Entscheidungsunterstützung. Es ersetzt Ihren Beschaffungs- oder Qualitätsprozess nicht, und Organisationen unter GxP sollten es weiterhin außerhalb ihrer validierten Systemgrenze behandeln.',
    },
    contact: {
      title: 'Sprechen Sie uns an',
      name: 'Ihr Name',
      message: 'Was wählen Sie aus?',
      send: 'Schreiben Sie uns',
      stepTopic: 'Worum geht es?',
      topicAccess: 'Zugang anfragen',
      topicSelection: 'Eine Auswahl besprechen',
      topicSecurity: 'Sicherheit und eigener Betrieb',
      topicPricing: 'Pläne und Preise',
      stepDetails: 'Eine Zeile Kontext',
      optional: 'optional',
      organisation: 'Organisation',
      context: 'Was wählen Sie aus?',
      organisationLine: 'Organisation: {value}',
      sheetTitle: 'Ihre Nachricht',
      sheetTo: 'An',
      sheetSubject: 'Betreff',
      subjectAccess: 'Zugangsanfrage: Hanseatic Select',
      subjectSelection: 'Eine Anbieterauswahl: Hanseatic Select',
      subjectSecurity: 'Sicherheit und Betrieb: Hanseatic Select',
      subjectPricing: 'Pläne und Preise: Hanseatic Select',
      introAccess: 'Ich möchte für meine Organisation Zugang zu Hanseatic Select erhalten.',
      introSelection: 'Ich möchte eine geplante Anbieterauswahl mit Ihnen besprechen.',
      introSecurity: 'Ich habe Fragen zu Sicherheit und zum Betrieb von Hanseatic Select in der eigenen Umgebung.',
      introPricing: 'Ich möchte wissen, welcher Plan zu meiner Organisation passt.',
      signOff: 'Mit freundlichen Grüßen',
      demoTitle: 'Lieber erst ansehen?',
      officeTitle: 'Hanseatic LifeScience Consulting',
    },
    legal: {
      imprint: 'Impressum',
      privacy: 'Datenschutz',
      terms: 'AGB',
      managingDirectors: 'Geschäftsführer',
      vatId: 'USt-IdNr.',
      responsible: 'Inhaltlich verantwortlich',
      draftNotice: 'Entwurf — vor dem Start rechtlich prüfen lassen.',
    },
    footer: {
      product: 'Produkt',
      company: 'Unternehmen',
      legal: 'Rechtliches',
      rights: 'Alle Rechte vorbehalten.',
      built: 'Von Life-Science-Berater:innen in Buchholz gebaut.',
    },
  },

  report: {
    title: 'Bericht',
    openCta: 'Bericht öffnen',
    contents: 'Inhalt',
    navOutcome: 'Ergebnis',
    navRanking: 'Ranking',
    navMethod: 'Gewichte und Methode',
    navEvidence: 'Nachweise',
    navCompliance: 'Konformität',
    navGaps: 'Abweichungen und Integrität',
    navStability: 'Stabilität',
    navSignoff: 'Freigabe und Audit-Trail',
    provisional: 'Vorläufig',
    filed: 'Dokumentiert',
    downloadTitle: 'Zum Mitnehmen',
    downloadPdf: 'Ausführlicher Bericht',
    downloadPdfDesc: 'Jeder Abschnitt, jeder Nachweis, der Audit-Trail und die Unterschriftenseite — druckfertig.',
    downloadDeck: 'Übersichtsfolien',
    downloadDeckDesc: 'Sieben Folien für einen Lenkungsausschuss: die Entscheidung, das Ranking, die Nachweise und wie stabil das Ergebnis ist.',
    downloadCsv: 'Bewertungsexport',
    downloadCsvDesc: 'Eine Zeile je Abschnitt, Kriterium und Anbieter, mit der kombinierten Wertung.',
    downloadRtm: 'Anforderungsmatrix',
    downloadRtmDesc: 'Eine Zeile je Anforderung, mit Konformität und Zitat jedes Anbieters.',
    outcome: 'Ergebnis',
    leading: 'Führend',
    sealTitle: 'Registersiegel',
    sealNone: 'NICHT VERSIEGELT — keine aktuelle Unterschrift deckt diesen Inhalt ab.',
    sealedBy: 'Versiegelt von {name} · {date}',
    method: 'Gewichte und Methode',
    methodEmpty: 'Es sind noch keine Abschnitte angelegt.',
    engineHeuristic: 'Unabhängige Durchsicht der Angebote, nur mit den integrierten Regeln — es wurde kein Sprachmodell verwendet.',
    engineActive: 'Unabhängige Durchsicht der Angebote, unterstützt durch ein Sprachmodell.',
    rankCol: 'Rang',
    vendorCol: 'Anbieter',
    weightedMean: 'Gewichtetes Mittel',
    sectionCol: 'Abschnitt',
    criterionCol: 'Kriterium',
    weightCol: 'Gewicht',
    sectionWeight: 'Abschnittsgewicht',
    criteriaTotal: 'Summe der Kriterien',
    sectionsTotal: 'Summe der Abschnitte',
    evidence: 'Nachweise',
    evidenceEmpty: 'Noch keine zitierten Nachweise vorhanden.',
    evidenceShowMore: '{n} weitere anzeigen',
    evidenceShowLess: 'Weniger anzeigen',
    compliance: 'Konformität',
    complianceEmpty: 'Noch keine Konformitätsmatrix vorhanden.',
    complianceOpen: 'Konformitätsmatrix öffnen',
    gaps: 'Abweichungen und Integrität',
    gapsEmpty: 'Keine auffällige Abweichung zwischen der unabhängigen Lesart und dem Mitwirkenden-Panel.',
    gapTypeCol: 'Typ',
    gapDeltaCol: 'Differenz',
    gapFlagged: 'Markiert',
    stability: 'Stabilität',
    stabilityEmpty: 'Noch keine Sensitivitätsanalyse verfügbar.',
    stabilityWinner: 'Sieger',
    stabilityMargin: 'Abstand zum Zweiten',
    stabilityCurrentCol: 'Aktuelles Gewicht',
    stabilitySummaryCol: 'Was das Ergebnis ändern würde',
    auditTitle: 'Audit-Trail',
    auditEmpty: 'Noch keine Audit-Historie vorhanden.',
  },

  present: {
    entryCta: 'Präsentieren',
    entryHint: 'Diese Bewertung live präsentieren',
    frameTitle: 'Präsentation der Übersichtsfolien',
    loading: 'Folien werden vorbereitet …',
    buildFailed: 'Die Übersichtsfolien konnten gerade nicht erstellt werden.',
    buildFailedLead: 'Nichts ist verloren — versuchen Sie es gleich noch einmal.',
    retryCta: 'Erneut versuchen',
    fullscreenEnter: 'Vollbild an',
    fullscreenExit: 'Vollbild aus',
    exitCta: 'Präsentation beenden',
  },
};
